import type { PayloadAction, SerializedError } from '@reduxjs/toolkit'
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import type { Nullable, ProjectId } from 'common/types'

import type { ProjectDetail } from '../../types'
import { constructUpdatedProject } from './helpers'
import {
  changeProjectState,
  getAllProjects,
  getAllProjectSizes,
  getProjectById,
} from './projectsActions'

export const projectsAdapter = createEntityAdapter<ProjectDetail>({
  selectId: project => project.id,
})

type SliceStatus = 'wait' | 'loading' | 'done' | 'failed'

export const projectsSlice = createSlice({
  name: 'projects',
  initialState: projectsAdapter.getInitialState<{
    status: SliceStatus
    error: Nullable<SerializedError>
  }>({
    status: 'wait',
    error: null,
  }),
  reducers: {
    projectsAdded(state, action: PayloadAction<ProjectDetail[]>) {
      projectsAdapter.upsertMany(state, action.payload)
    },
    projectRemoved(state, action: PayloadAction<ProjectId[]>) {
      projectsAdapter.removeMany(state, action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAllProjectSizes.fulfilled, (state, action) => {
        action.payload.forEach(({ id, size }) => {
          const project = state.entities[id]

          if (project) {
            project.size = size
          }
        })
      })
      .addCase(getAllProjects.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getAllProjects.fulfilled, (state, { payload: newProjects }) => {
        if (state.ids.length === newProjects.length) {
          newProjects.forEach(updatedProject => {
            state.entities[updatedProject.id] = constructUpdatedProject(
              updatedProject,
              state.entities[updatedProject.id],
            )
          })
        } else {
          projectsAdapter.addMany(state, newProjects)
        }
        state.status = 'done'
      })
      .addCase(getAllProjects.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error
      })
    builder
      .addCase(changeProjectState.pending, state => {
        state.status = 'loading'
      })
      .addCase(changeProjectState.fulfilled, (state, { payload: { projectId, newState } }) => {
        projectsAdapter.updateOne(state, { id: projectId, changes: { state: newState } })
        state.status = 'done'
      })
      .addCase(changeProjectState.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error
      })

    builder
      .addCase(getProjectById.pending, state => {
        state.status = 'loading'
      })
      .addCase(getProjectById.fulfilled, (state, action) => {
        projectsAdapter.upsertOne(state, action.payload)
        state.status = 'done'
      })
      .addCase(getProjectById.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error
      })
  },
})

export default projectsSlice.reducer
